import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { WebAuthPanel } from "@/components/WebAuthPanel";
import {
  IdCard, ArrowLeft, Search, Loader2, RefreshCw, Crown, Gauge, Bell, FileSearch,
  MessageSquare, Phone, CalendarClock, FolderOpen, Files, HardDrive, Gift,
} from "lucide-react";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

const PLAN_STYLE = {
  free: "bg-slate-700/40 text-slate-300 ring-slate-500/30",
  pro: "bg-blue-600/20 text-blue-300 ring-blue-500/40",
  elite: "bg-amber-500/15 text-amber-300 ring-amber-500/40",
  complimentary: "bg-emerald-500/15 text-emerald-300 ring-emerald-500/40",
};

function PlanBadge({ plan }) {
  const p = plan || "free";
  if (p === "complimentary") {
    return (
      <span data-testid="plan-badge-complimentary" className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1 ${PLAN_STYLE.complimentary}`}>
        <Gift className="h-2.5 w-2.5" />Complimentary (unlimited)
      </span>
    );
  }
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1 ${PLAN_STYLE[p] || PLAN_STYLE.free}`}>
      {p === "elite" && <Crown className="h-2.5 w-2.5" />}{p}
    </span>
  );
}

const lim = (n) => (n < 0 ? "Unlimited" : n);

export default function Members() {
  const [list, setList] = useState([]);
  const [plans, setPlans] = useState([]);
  const [active, setActive] = useState(null);
  const [detail, setDetail] = useState(null);
  const [search, setSearch] = useState("");
  const [loadingList, setLoadingList] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const loadList = useCallback(async () => {
    setLoadingList(true);
    try {
      const { data } = await api.get("/members", { params: search ? { search } : {} });
      setList(data);
    } catch { toast.error("Failed to load members"); } finally { setLoadingList(false); }
  }, [search]);

  const loadPlans = useCallback(async () => {
    try { const { data } = await api.get("/plans"); setPlans(data); } catch {}
  }, []);

  const loadDetail = useCallback(async (phone) => {
    if (!phone) return;
    setLoadingDetail(true);
    try {
      const { data } = await api.get(`/members/${encodeURIComponent(phone)}`);
      setDetail(data);
    } catch { setDetail(null); toast.error("Failed to load member"); } finally { setLoadingDetail(false); }
  }, []);

  useEffect(() => { loadPlans(); }, [loadPlans]);
  useEffect(() => { const t = setTimeout(loadList, 250); return () => clearTimeout(t); }, [loadList]);
  useEffect(() => { if (active) loadDetail(active); }, [active, loadDetail]);

  const [planUntil, setPlanUntil] = useState("");
  const changePlan = async (plan) => {
    if (!detail?.member) return;
    try {
      await api.patch(`/members/${encodeURIComponent(detail.member.phone)}`, { plan, plan_until: planUntil ? `${planUntil}T23:59:59+05:30` : null });
      toast.success(`Plan updated to ${plan}`);
      loadDetail(detail.member.phone);
      loadList();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const changeStatus = async (status) => {
    if (!detail?.member) return;
    try {
      await api.patch(`/members/${encodeURIComponent(detail.member.phone)}`, { status });
      toast.success(`Member ${status}`);
      loadDetail(detail.member.phone);
      loadList();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const [compAsk, setCompAsk] = useState(null);
  const [grantPhone, setGrantPhone] = useState("");
  const setComp = async (phone, on) => {
    try {
      await api.put(`/members/${encodeURIComponent(phone)}/complimentary`, { on });
      toast.success(on ? "Complimentary access on" : "Complimentary access off. Back on Free");
      if (active === phone) loadDetail(phone);
      loadList();
      return true;
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); return false; }
  };
  const grant = async (e) => {
    e.preventDefault();
    const p = grantPhone.replace(/[\s-]/g, "");
    if (p && await setComp(p, true)) setGrantPhone("");
  };

  const timeline = buildTimeline(detail);

  return (
    <div className="min-h-screen bg-[#0B0F17] pb-16 text-slate-100">
      <header className="sticky top-0 z-30 border-b border-white/10 bg-[#0B0F17]/80 backdrop-blur-md">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-600/15 ring-1 ring-blue-500/30">
              <IdCard className="h-5 w-5 text-blue-400" />
            </div>
            <div>
              <h1 className="font-display text-xl font-bold tracking-tight text-slate-50">Members</h1>
              <p className="font-mono-data text-[11px] text-slate-500">Lawyer profiles · plans · activity</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="refresh-members" onClick={() => { loadList(); if (active) loadDetail(active); }}
              className="flex items-center gap-1.5 rounded-md bg-slate-700 px-3 py-1.5 text-sm text-white transition-colors hover:bg-slate-600">
              <RefreshCw className="h-4 w-4" /> Refresh
            </button>
            <Link to="/" data-testid="back-to-dashboard"
              className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm text-slate-400 transition-colors hover:bg-slate-800 hover:text-white">
              <ArrowLeft className="h-4 w-4" /> Dashboard
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto grid max-w-[1400px] grid-cols-1 gap-5 px-5 pt-6 lg:grid-cols-12">
        <div className="lg:col-span-12"><WebAuthPanel /></div>
        {/* List */}
        <div className="lg:col-span-5">
          <div className="overflow-hidden rounded-lg border border-white/10 bg-[#111723]">
            <form onSubmit={grant} data-testid="comp-grant-form" className="flex items-center gap-2 border-b border-white/10 p-3">
              <Gift className="h-4 w-4 shrink-0 text-emerald-400" />
              <input data-testid="comp-grant-input" value={grantPhone} onChange={(e) => setGrantPhone(e.target.value)}
                placeholder="Give complimentary access: +91..."
                className="w-full bg-transparent text-sm text-slate-200 placeholder:text-slate-600 focus:outline-none" />
              <button type="submit" data-testid="comp-grant-submit" className="shrink-0 rounded-md bg-emerald-600/80 px-3 py-1 text-xs font-medium text-white transition-colors hover:bg-emerald-600">Grant</button>
            </form>
            <div className="flex items-center gap-2 border-b border-white/10 p-3">
              <Search className="h-4 w-4 text-slate-500" />
              <input data-testid="member-search" value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name or number"
                className="w-full bg-transparent text-sm text-slate-200 placeholder:text-slate-600 focus:outline-none" />
              <span className="rounded-full bg-slate-800 px-2 py-0.5 font-mono-data text-xs text-slate-400">{list.length}</span>
            </div>
            <div className="max-h-[72vh] overflow-y-auto" data-testid="member-list">
              {loadingList && <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-slate-500" /></div>}
              {!loadingList && list.length === 0 && (
                <p className="px-4 py-10 text-center text-sm text-slate-500">No members yet. Profiles appear when lawyers message Dicta on WhatsApp.</p>
              )}
              {list.map((m) => (
                <button key={m.phone} data-testid={`member-item-${m.phone}`} onClick={() => setActive(m.phone)}
                  className={`flex w-full items-center justify-between border-b border-white/5 px-4 py-3 text-left transition-colors ${active === m.phone ? "bg-[#1A2234]" : "hover:bg-[#161d2b]"}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-semibold text-slate-100">{m.name || "Unknown lawyer"}</span>
                      {m.status === "suspended" && <span className="rounded bg-red-500/15 px-1.5 py-0.5 text-[9px] uppercase text-red-400">suspended</span>}
                    </div>
                    <div className="mt-0.5 font-mono-data text-[11px] text-blue-400">{m.phone}</div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <PlanBadge plan={m.plan} />
                    {m.upgrade_requested_at && (
                      <span data-testid={`upgrade-requested-${m.phone}`} className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[9px] font-semibold uppercase text-amber-400">Upgrade requested</span>
                    )}
                    <span className="text-[10px] text-slate-500">{m.last_seen_at ? fmt(m.last_seen_at).split(",")[0] : "not messaged yet"}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Detail */}
        <div className="lg:col-span-7">
          {!active ? (
            <div className="flex h-[76vh] items-center justify-center rounded-lg border border-white/10 bg-[#111723] text-sm text-slate-500">
              Select a member to view their profile.
            </div>
          ) : loadingDetail || !detail ? (
            <div className="flex h-[76vh] items-center justify-center rounded-lg border border-white/10 bg-[#111723]">
              <Loader2 className="h-5 w-5 animate-spin text-slate-500" />
            </div>
          ) : (
            <div className="space-y-5">
              {/* Overview + plan */}
              <div className="rounded-lg border border-white/10 bg-[#111723] p-5">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h2 className="font-display text-lg font-bold text-slate-50">{detail.member.name || "Unknown lawyer"}</h2>
                    <div className="mt-1 flex items-center gap-1.5 font-mono-data text-xs text-blue-400"><Phone className="h-3 w-3" /> {detail.member.phone}</div>
                    <div className="mt-1 flex items-center gap-1.5 text-[11px] text-slate-500"><CalendarClock className="h-3 w-3" /> Member since {fmt(detail.member.created_at)}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <PlanBadge plan={detail.member.plan} />
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${detail.member.status === "active" ? "bg-emerald-500/15 text-emerald-400" : "bg-red-500/15 text-red-400"}`}>{detail.member.status}</span>
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <Stat icon={FileSearch} label="Lookups (mo)" value={`${detail.member.usage_lookups} / ${lim(detail.limits.lookups_per_month)}`} />
                  <Stat icon={Bell} label="Watches" value={`${detail.watches.length} / ${lim(detail.limits.watches_max)}`} />
                  <Stat icon={Gauge} label="Research" value={detail.limits.legal_research ? "Included" : "Locked"} />
                  <Stat icon={MessageSquare} label="Messages" value={detail.conversation?.messages?.length || 0} />
                </div>

                <div data-testid="member-store-stats" className="mt-3 grid grid-cols-3 gap-3">
                  <Stat icon={FolderOpen} label="Matters" value={detail.store?.matters ?? 0} />
                  <Stat icon={Files} label="Saved files" value={`${detail.store?.files ?? 0}${detail.store?.library ? ` (${detail.store.library} in Library)` : ""}`} />
                  <Stat icon={HardDrive} label="Storage used" value={fmtBytes(detail.store?.bytes || 0)} />
                </div>

                <div data-testid="member-billing-status" className="mt-3 rounded-lg border border-white/10 bg-[#0B0F17] p-3 text-xs text-slate-400">
                  <span className="uppercase tracking-wide text-slate-500">Billing</span>{" "}
                  <BillingChip status={billingStatus(detail.billing)} />{" "}
                  {detail.billing?.mode === "manual" && <span>manual plan{detail.billing?.manual_until ? ` until ${detail.billing.manual_until.slice(0, 10)}` : ""}</span>}
                  {detail.billing?.mode !== "manual" && detail.billing?.paid_until && <span>paid until {detail.billing.paid_until.slice(0, 10)}</span>}
                </div>

                <div className="mt-5 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-3">
                  <div>
                    <p className="text-sm font-medium text-slate-100">Complimentary (unlimited)</p>
                    <p className="text-xs text-slate-400">No limits, no billing, no upgrade prompts. Off puts the number back on Free.</p>
                  </div>
                  <Switch data-testid="member-comp-toggle" checked={!!detail.member.complimentary}
                    onCheckedChange={(v) => setCompAsk({ phone: detail.member.phone, on: v })} />
                </div>

                <div className="mt-5 flex flex-wrap items-center gap-3 border-t border-white/10 pt-4">
                  <span className="text-xs text-slate-400">Plan</span>
                  <Select value={detail.member.plan} onValueChange={changePlan} disabled={!!detail.member.complimentary}>
                    <SelectTrigger data-testid="member-plan-select" className="h-9 w-36 border-white/10 bg-[#0B0F17] text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="border-white/10 bg-[#111723] text-slate-100">
                      {detail.member.complimentary && <SelectItem value="complimentary">Complimentary</SelectItem>}
                      {plans.map((p) => (
                        <SelectItem key={p.id} value={p.id} data-testid={`plan-option-${p.id}`}>{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <span className="text-xs text-slate-400">Manual until</span>
                  <input type="date" data-testid="member-plan-until-input" value={planUntil} onChange={(e) => setPlanUntil(e.target.value)}
                    className="h-9 rounded-md border border-white/10 bg-[#0B0F17] px-2 text-sm text-slate-200" />
                  <span className="ml-2 text-xs text-slate-400">Status</span>
                  <Select value={detail.member.status} onValueChange={changeStatus}>
                    <SelectTrigger data-testid="member-status-select" className="h-9 w-36 border-white/10 bg-[#0B0F17] text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="border-white/10 bg-[#111723] text-slate-100">
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="suspended">Suspended</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Activity timeline */}
              <div className="overflow-hidden rounded-lg border border-white/10 bg-[#111723]">
                <div className="border-b border-white/10 p-4">
                  <h3 className="font-display text-base font-bold text-slate-50">Activity Timeline</h3>
                  <p className="mt-0.5 text-xs text-slate-400">Case lookups, hearing-alert watches, and conversation</p>
                </div>
                <div className="max-h-[52vh] space-y-2 overflow-y-auto p-4" data-testid="member-timeline">
                  {timeline.length === 0 && <p className="py-8 text-center text-sm text-slate-500">No activity yet.</p>}
                  {timeline.map((t, i) => (
                    <div key={i} className="flex items-start gap-3 rounded-lg border border-white/10 bg-[#0B0F17] p-3">
                      <div className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md ${t.tint}`}>
                        <t.icon className="h-3.5 w-3.5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm text-slate-200">{t.title}</span>
                          <span className="shrink-0 text-[10px] text-slate-500">{fmt(t.at)}</span>
                        </div>
                        {t.sub && <p className="mt-0.5 truncate text-xs text-slate-500">{t.sub}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
      <AlertDialog open={!!compAsk} onOpenChange={(o) => { if (!o) setCompAsk(null); }}>
        <AlertDialogContent data-testid="comp-confirm-dialog" className="border-white/10 bg-[#111723] text-slate-100">
          <AlertDialogHeader>
            <AlertDialogTitle>{compAsk?.on ? "Turn on complimentary access?" : "Turn off complimentary access?"}</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              {compAsk?.on
                ? "This number gets unlimited Dicta with no charges. Any auto-renewal is stopped first. No message is sent to the lawyer."
                : "This number goes back to the Free plan with normal limits from now. Saved files stay saved. No message is sent to the lawyer."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="comp-confirm-cancel" className="border-white/10 bg-transparent text-slate-300">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="comp-confirm-ok" onClick={() => { const a = compAsk; setCompAsk(null); setComp(a.phone, a.on); }}>
              {compAsk?.on ? "Turn on" : "Turn off"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export function billingStatus(b) {
  if (b?.comp) return "complimentary";
  if (!b || !b.mode) return "none";
  if (b.mode === "manual") return "manual";
  if (b.mode === "one_time") return b.status === "ended" ? "ended" : "one-time";
  if (b.cancel_requested && b.status === "active") return "cancelled";
  return { setting_up: "setting up", retrying: "retrying" }[b.status] || b.status || "none";
}

const CHIP = {
  "setting up": "bg-sky-500/15 text-sky-300", active: "bg-emerald-500/15 text-emerald-300",
  retrying: "bg-amber-500/15 text-amber-300", halted: "bg-red-500/15 text-red-300",
  cancelled: "bg-slate-500/20 text-slate-300", completed: "bg-slate-500/20 text-slate-300",
  "one-time": "bg-violet-500/15 text-violet-300", paid: "bg-emerald-500/15 text-emerald-300",
  created: "bg-slate-700/40 text-slate-400", manual: "bg-fuchsia-500/15 text-fuchsia-300",
  complimentary: "bg-emerald-500/15 text-emerald-300",
};

export function BillingChip({ status, testid }) {
  return <span data-testid={testid || "billing-status-chip"} className={`rounded px-2 py-0.5 text-[11px] font-medium ${CHIP[status] || "bg-slate-700/40 text-slate-400"}`}>{status}</span>;
}

function fmtBytes(n) {
  if (n >= 1073741824) return `${(n / 1073741824).toFixed(2)} GB`;
  if (n >= 1048576) return `${(n / 1048576).toFixed(1)} MB`;
  return `${Math.round(n / 1024)} KB`;
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-lg border border-white/10 bg-[#0B0F17] p-3">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-slate-500"><Icon className="h-3 w-3" /> {label}</div>
      <div className="mt-1 font-display text-lg font-bold text-slate-100">{value}</div>
    </div>
  );
}

function buildTimeline(detail) {
  if (!detail) return [];
  const items = [];
  (detail.lookups || []).forEach((r) => items.push({
    at: r.created_at,
    icon: FileSearch,
    tint: r.status === "replied" ? "bg-emerald-500/15 text-emerald-400" : "bg-slate-600/30 text-slate-400",
    title: `Case lookup · ${r.case_number}`,
    sub: `${r.status}${r.source ? " · " + r.source : ""}`,
  }));
  (detail.watches || []).forEach((w) => items.push({
    at: w.created_at,
    icon: Bell,
    tint: "bg-blue-600/20 text-blue-300",
    title: `Hearing-alert watch · ${w.cnr}`,
    sub: w.auto_added ? "auto-added from WhatsApp query" : "added manually",
  }));
  const msgs = detail.conversation?.messages || [];
  if (msgs.length) {
    const last = msgs[msgs.length - 1];
    items.push({
      at: last.at || detail.conversation.updated_at,
      icon: MessageSquare,
      tint: "bg-indigo-500/15 text-indigo-300",
      title: `Conversation · ${msgs.length} messages`,
      sub: (last.content || "").slice(0, 70),
    });
  }
  return items.sort((a, b) => new Date(b.at || 0) - new Date(a.at || 0));
}
